import { Text } from '@react-three/drei'
import { useAppStore } from '@store'
import { SceneObjects, Walls } from '@types'
import { toVector3 } from '@utils/conversion/toVector3'
import { calcRotation } from '@utils/generalMaths/calcRotation'
import is2DMode from '@utils/is2DMode'
import isItem from '@utils/sceneObjects/assertions/isItem'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import getId from '@utils/sceneObjects/getId'
import { getInnerAngle } from '@utils/sceneObjects/walls/getInnerAngle'
import { useMemo } from 'react'
import VerticalLine from './VerticalLine'
type Props = { id: string; offset: number; elements: SceneObjects[] }
export default function ItemDimentions({ id, offset, elements }: Props) {
  const item = useAppStore((store) => store.items.get(id)) as Walls
  const items = useAppStore.use.items()
  const width = item.width
  const start = item.start
  const end = item.end
  const rotation = item.rotation
  const unit = useAppStore((store) => store.prefs?.UNIDADMEDIDA)
  const PREFS_COLOR_COTA = useAppStore((store) => store.prefs?.['C/LINEASCOTA'])
  const HORIZONTAL_WALL_ARGS = [width, 7] satisfies [
    width?: number | undefined,
    height?: number | undefined,
    depth?: number | undefined,
    widthSegments?: number | undefined,
    heightSegments?: number | undefined,
    depthSegments?: number | undefined,
  ]
  const prevWall = item.prevWall
  const prevWallObject = useAppStore.use.items().get(prevWall) as Walls
  const { cp } = getInnerAngle(start, end, prevWallObject?.start, prevWallObject?.end)
  const thickness = item.thickness
  const PREFS_COLOR_TEXTO = useAppStore((store) => store.prefs?.['C/COTA'])
  const FONT_SIZE = useAppStore.use.prefs()?.['T/LETRACOTA'] ?? 100
  const neededRotation = calcRotation(cp, rotation)
  const wallStart = toVector3(item.start)
  const wallWidth = item.width
  const pref = useAppStore.use.prefs()?.['VERUNIDADESENCOTAS']
  const mode = useAppStore.use.mode()
  const is2dmode = is2DMode(mode)
  const offsets = is2dmode ? -thickness - offset : thickness + offset + 200
  const elementArray = useMemo(
    () =>
      Array.from(elements)
        .map((el) => items.get(el))
        .filter((el): el is NonNullable<typeof el> => el !== undefined)
        .filter((el) => isItem(el) || isLinkedItem(el))
        .filter((el) => {
          const idUnidades = Number((el as any)?.idUnidades || 0)
          const catalog = String((el as any)?.catalogID || '').toUpperCase()
          return (
            idUnidades !== 7719 &&
            idUnidades !== 7720 &&
            !catalog.includes('COJIN') &&
            !catalog.includes('COJINES')
          )
        })
        .filter((el) => (el as any).canCollide !== false)
        //TODO: This may be good to be refactored into a reduce
        .sort((a, b) => {
          return (
            wallStart
              .clone()
              .sub(toVector3([a.x, a.z]))
              .length() -
            wallStart
              .clone()
              .sub(toVector3([b.x, b.z]))
              .length()
          )
        })
        .flatMap((el, i, arr) => {
          const elWidth = el.width ?? 0
          const elDistI = wallStart
            .clone()
            .sub(toVector3([el.x, el.z]))
            .length()
          const elDistD = wallWidth - elDistI - elWidth

          if (i === 0 && arr.length !== 1) {
            return [
              {
                distI: 0,
                distD: elDistI + elDistD,
                width: elDistI,
                id: getId('holes'),
                type: 'holes',
                x: 0,
                z: 0,
              },
              { ...el, distI: elDistI, distD: elDistI + elWidth, width: elWidth },
            ]
          }
          if (i === 0 && arr.length === 1) {
            return [
              {
                distI: 0,
                distD: elDistI,
                width: elDistI,
                id: getId('holes'),
                type: 'holes',
                x: 0,
                z: 0,
              },
              elWidth + elDistI > wallWidth
                ? { width: 0, distI: 0, distD: 0, id: getId('holes'), type: 'holes', x: 0, z: 0 }
                : { ...el, distI: elDistI, distD: elDistD, width: elWidth },
              {
                distI: elDistI + elWidth,
                distD: 0,
                width: elDistD,
                type: 'holes',
                id: getId('holes'),
                x: 0,
                z: 0,
              },
            ]
          }
          if (i === arr.length - 1) {
            const prev = arr[i - 1]
            if (!prev) return null

            const prevDistI = wallStart
              .clone()
              .sub(toVector3([prev.x, prev.z]))
              .length()
            const prevItemWidthMorph = prev.morphs?.find(
              (morph) => morph.name === 'Ancho' || morph.name === 'width',
            )
            const prevWidth = prevItemWidthMorph?.default ?? prev.width ?? 0

            return [
              {
                type: 'holes',
                distI: prevDistI + prevWidth,
                distD: elDistI,
                width: elDistI - (prevDistI + prevWidth),
                id: getId('holes'),
                x: 0,
                z: 0,
              },
              { ...el, distI: elDistI, distD: elDistD, width: elWidth },
              {
                type: 'holes',
                distI: elDistI + elWidth,
                distD: 0,
                width: width - elDistI - elWidth,
                id: getId('holes'),
                x: 0,
                z: 0,
              },
            ]
          }
          const prev = arr[i - 1]
          if (!prev) return null

          const prevDistI = wallStart
            .clone()
            .sub(toVector3([prev.x, prev.z]))
            .length()
          const prevWidthMorph = prev.morphs?.find(
            (morph) => morph.name === 'Ancho' || morph.name === 'width',
          )
          const prevWidth = prevWidthMorph?.default ?? prev.width ?? 0

          return [
            {
              type: 'holes',
              distI: prevDistI + prevWidth,
              distD: elDistI,
              width: elDistI - (prevDistI + prevWidth),
              id: getId('holes'),
              x: 0,
              z: 0,
            },
            { ...el, distI: elDistI, distD: elDistD, width: elWidth },
          ]
        })
        .filter(Boolean),
    [elements, items, wallStart, wallWidth, width],
  )
  return (
    <group>
      {elementArray.map((el) => {
        if (!el) return null
        let distX = -width / 2 + el.distI
        if (distX < -width / 2) {
          distX = -width / 2
        }

        const elWidth = typeof el.width === 'number' ? el.width : 0

        return (
          <group key={`inside-item-${el.distI}-${el.distD}-${el.id}-${el.x}-${el.z}}`}>
            <VerticalLine position={[distX, 2, offsets]} />
            <VerticalLine position={[distX + elWidth, 2, offsets]} />
            <mesh
              position={[0, 2, offsets]}
              rotation={[-Math.PI / 2, 0, 0]}
            >
              <boxGeometry args={HORIZONTAL_WALL_ARGS} />
              <meshBasicMaterial color={PREFS_COLOR_COTA} />
              {elWidth && elWidth > 1 && (
                <Text
                  position={[-width / 2 + el.distI + elWidth / 2, 75, 0]}
                  fontSize={FONT_SIZE}
                  characters='abcdefghijklmnopqrstuvwxyz0123456789!ºª'
                  color={PREFS_COLOR_TEXTO}
                  rotation={[0, ...neededRotation]}
                >
                  {`${Math.trunc(elWidth)} ${pref ? unit : ''}`}
                </Text>
              )}
            </mesh>
          </group>
        )
      })}
    </group>
  )
}
